import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import authService from '../services/authService';
import MfaSetup from '../components/auth/MfaSetup';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import { formatDate } from '../utils/formatters';
import { ROLES } from '../utils/constants';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const ProfileSection = ({ user, refreshProfile }) => {
  const [values, setValues] = useState({ first_name: user.first_name || '', last_name: user.last_name || '', mobile: '' });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const payload = { first_name: values.first_name, last_name: values.last_name };
      if (values.mobile.trim()) payload.mobile = values.mobile.trim();
      await authService.updateProfile(payload);
      await refreshProfile();
      setValues((v) => ({ ...v, mobile: '' }));
      setNotice({ type: 'success', message: 'Your details have been updated.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Your details could not be saved.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <h2>Your details</h2>
        {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" value={user.email} disabled />
          </div>
          <FormField label="First name" name="first_name" value={values.first_name} onChange={(e) => setValues((v) => ({ ...v, first_name: e.target.value }))} required />
          <FormField label="Last name" name="last_name" value={values.last_name} onChange={(e) => setValues((v) => ({ ...v, last_name: e.target.value }))} required />
          <FormField
            label="Mobile number"
            name="mobile"
            type="tel"
            value={values.mobile}
            onChange={(e) => setValues((v) => ({ ...v, mobile: e.target.value }))}
            placeholder={user.mobile || '0821234567'}
            helperText={user.mobile ? `Current: ${user.mobile}. Leave blank to keep it.` : undefined}
          />
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </form>
      </div>
    </div>
  );
};

const PasswordSection = ({ user }) => {
  if (user.passwordless) return null;

  const [values, setValues] = useState({ currentPassword: '', password: '', passwordConfirmation: '' });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      await authService.changePassword(values);
      setValues({ currentPassword: '', password: '', passwordConfirmation: '' });
      setNotice({ type: 'success', message: 'Your password has been changed. Other devices have been signed out.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Your password could not be changed.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <h2>Change password</h2>
        {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}
        <form onSubmit={submit}>
          <FormField label="Current password" name="currentPassword" type="password" value={values.currentPassword} onChange={(e) => setValues((v) => ({ ...v, currentPassword: e.target.value }))} required />
          <FormField label="New password" name="password" type="password" value={values.password} onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))} required />
          <FormField label="Confirm new password" name="passwordConfirmation" type="password" value={values.passwordConfirmation} onChange={(e) => setValues((v) => ({ ...v, passwordConfirmation: e.target.value }))} required />
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Change password'}</button>
        </form>
      </div>
    </div>
  );
};

const DisableMfaModal = ({ onClose, onDisabled }) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await authService.disableMfa(password);
      onDisabled();
    } catch (err) {
      setError(messageFrom(err, 'Two-factor authentication could not be turned off.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Turn off two-factor authentication">
      <form onSubmit={submit}>
        <p className="ap-muted">Confirm your password to turn off two-factor authentication for this account.</p>
        {error && <Alert type="error" message={error} />}
        <FormField label="Password" name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button type="submit" className="btn-primary" disabled={busy || !password}>{busy ? 'Turning off…' : 'Turn off'}</button>
      </form>
    </Modal>
  );
};

const SecuritySection = ({ user, refreshProfile }) => {
  const [settingUp, setSettingUp] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [notice, setNotice] = useState(null);

  if (settingUp) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <MfaSetup onComplete={async () => { setSettingUp(false); await refreshProfile(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <h2>Two-factor authentication</h2>
        {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}
        {user.mfa_enabled ? (
          <>
            <p>
              Enabled via {user.mfa_method === 'email' ? 'email codes' : 'an authenticator app'}.
              {user.mfa_required && ' This is required for your role and cannot be turned off.'}
            </p>
            {!user.mfa_required && (
              <button type="button" className="btn-secondary" onClick={() => setDisabling(true)}>Turn off</button>
            )}
          </>
        ) : (
          <>
            <p>Not enabled. Add a second step when signing in, using an authenticator app or an emailed code.</p>
            <button type="button" className="btn-primary" onClick={() => setSettingUp(true)}>Set up two-factor authentication</button>
          </>
        )}
      </div>
      {disabling && (
        <DisableMfaModal
          onClose={() => setDisabling(false)}
          onDisabled={async () => {
            setDisabling(false);
            await refreshProfile();
            setNotice({ type: 'success', message: 'Two-factor authentication has been turned off.' });
          }}
        />
      )}
    </div>
  );
};

const AccountPage = () => {
  const { user, userRole, refreshProfile } = useAuth();

  if (!user) return null;

  return (
    <div className="account-page" style={{ padding: 20, maxWidth: 640 }}>
      <h1>Account</h1>
      <p className="ap-muted">Member since {formatDate(user.created_at)}.</p>

      <ProfileSection user={user} refreshProfile={refreshProfile} />
      <PasswordSection user={user} />
      <SecuritySection user={user} refreshProfile={refreshProfile} />

      <div className="card">
        <div className="card-body">
          <h2>Legal</h2>
          <p><Link to="/terms">Terms and Conditions</Link></p>
          <p><Link to="/privacy">Privacy Policy</Link></p>
          {userRole === ROLES.APPLICANT && <p><Link to="/my-data">My data</Link></p>}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
